import { type RefObject, useEffect, useMemo, useRef } from 'react';
import { paths } from '../../../common/constants/pathVariables.js';
import { categoryMsg, nameMsg, subscriptionMsg, t, typeMsg } from '../../../common/i18n/index.js';
import { OpenFormCheckbox } from '../OpenFormComponents/OpenFormCheckbox.js';
import { OpenFormGridRow } from '../OpenFormComponents/OpenFormGrid.js';
import { useOpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { useTable } from '../OpenFormHooks/useTable.js';
import { useOpenFormAsync } from '../OpenFormProvider.js';
import { OpenFormListTable, OpenFormListTableLabel } from './OpenFormListTable.js';

export const OpenFormListTableForm = ({
  disabled,
  selected,
  select,
  set,
  sizes = [25, 50, 100],
}: {
  disabled: boolean;
  selected: Set<string>;
  select: (set?: Set<string>) => void;
  set: RefObject<Set<string>>;
  sizes?: readonly number[];
}) => {
  const async = useOpenFormAsync();
  const goto = useOpenFormGoto();
  const items = useMemo(() => Array.from(async.forms.values()), [async]);
  const list = useRef(items);
  const [cols, rows, view, sort] = useTable({
    cols: stats => [
      {
        sortable: false,
        visible: !async.flags.has('draft'),
        key: 'checkbox',
        label: (
          <OpenFormCheckbox
            disabled={async.state.disabled || !stats.current.length}
            indeterminate={set.current.size > 0 && set.current.size < stats.current.length}
            checked={set.current.size > 0 && set.current.size === stats.current.length}
            onChange={() => {
              if (rows.current.every(form => !async.store.hasItem(form.id) || set.current.has(form.id))) {
                rows.current.forEach(form => !async.store.hasItem(form.id) || set.current.delete(form.id));
              } else {
                rows.current.forEach(form => !async.store.hasItem(form.id) || set.current.add(form.id));
              }
              select(set.current);
            }}
          />
        ),
      },
      { sortable: true, visible: true, key: 'form', label: t.PNS1(nameMsg), sorted: 'asc' },
      { sortable: true, visible: true, key: 'type', label: t.F9G1(typeMsg) },
      { sortable: true, visible: true, key: 'productCategory', label: t.CV5H(categoryMsg) },
      { sortable: true, visible: true, key: 'subscriptionType', label: t.P674(subscriptionMsg) },
      { sortable: false, visible: false, key: 'name', label: null },
      { sortable: false, visible: false, key: 'id', label: null },
    ],
    rows: () =>
      list.current.map(({ id, name, productCategoryDesc, subscriptionType, typeDesc }) => ({
        checkbox: (
          <OpenFormCheckbox
            checked={set.current.has(id)}
            disabled={async.state.disabled || !async.store.hasItem(id)}
            onChange={() => {
              set.current.has(id) ? set.current.delete(id) : set.current.add(id);
              select(set.current);
            }}
          />
        ),
        form: (
          <OpenFormListTableLabel
            disabled={async.state.disabled}
            label={name}
            onClick={() => goto({ formId: id }, { list: paths.OPENFORM_LIST_FORM })}
          />
        ),
        type: typeDesc ?? '',
        productCategory: productCategoryDesc ?? '',
        subscriptionType: subscriptionType ?? '',
        name: name,
        id: id,
      })),
    view: () => ({ size: sizes[0], total: list.current.length }),
    order: 'name',
  });

  useEffect(() => sort.current(), [disabled, selected, sort]);

  return (
    <OpenFormGridRow justifyCenter>
      <OpenFormListTable
        cols={cols}
        disabled={disabled}
        rows={rows}
        sizes={sizes}
        sort={sort}
        view={view}
        onPageChange={select}
        onSizeChange={select}
        onSortChange={select}
      />
    </OpenFormGridRow>
  );
};
